import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiPlannedContainerAction } from '@Model';
import { PlannedContainerActionRepository } from '@Repositories/Auftragsbearbeitung';
import {
    EnumFilterExpressionOperation,
    EnumOperation,
    isOk,
    type ListRequest,
} from '@synqo/frontend-component-library';
import { useQuery } from '@tanstack/react-query';

export interface PlannedAtSiteRequest {
    siteId: string | undefined;
    customerId: string | undefined;
    /** When the order was last saved. A change re-reads the placements. */
    savedAt: string;
}

export interface PlannedAtSiteState {
    entries: ApiPlannedContainerAction[];
    failed: boolean;
    /** Whether an answer arrived, failed or not. */
    loaded: boolean;
}

const NO_ENTRIES: ApiPlannedContainerAction[] = [];

/** Everything open orders plan for a customer at a site: placements, pickups, swaps and emptyings. */
// @ai-generated 2026-10-15T12:34:04+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function usePlannedAtSite({ siteId, customerId, savedAt }: PlannedAtSiteRequest): PlannedAtSiteState {
    const plannedActions = useAuftragRepository(PlannedContainerActionRepository);
    const canBeRead = !!siteId && !!customerId;

    const { data } = useQuery({
        queryKey: ['planned-at-site', siteId, customerId, savedAt],
        queryFn: () => plannedActions.getPlannedActions(plannedAt(siteId ?? '', customerId ?? '')),
        enabled: canBeRead,
        // A new key must not blank the sidebar while its answer is on the way, unless site or customer changed.
        placeholderData: (previous, previousQuery) => {
            const sameSite = previousQuery?.queryKey[1] === siteId && previousQuery?.queryKey[2] === customerId;

            return sameSite ? previous : undefined;
        },
    });

    if (!canBeRead || data === undefined) {
        return { entries: NO_ENTRIES, failed: false, loaded: false };
    }

    if (!isOk(data)) {
        return { entries: NO_ENTRIES, failed: true, loaded: true };
    }

    return { entries: data.data.items, failed: false, loaded: true };
}

// @ai-generated 2026-10-15T12:34:04+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
function plannedAt(siteId: string, customerId: string): ListRequest {
    return {
        page_no: 0,
        page_size: 100,
        full_text_search: '',
        sort_columns: [],
        filter_columns: {
            operation: EnumFilterExpressionOperation.And,
            filters: [
                { field: 'site.foreign_key', value: siteId, operator: EnumOperation.Equal },
                { field: 'customer.foreign_key', value: customerId, operator: EnumOperation.Equal },
            ],
        },
    };
}
